/**
 * ContextMap — "The Constellation".
 *
 * A touch-native, pure-DOM replacement for the old D3 chord diagram (which was
 * hover/zoom-driven and unusable on a phone). Renders:
 *   1. A "These connect" strip — cross-thread insights as tappable pill-pairs
 *      that expand inline to the full insight text.
 *   2. One full-width card per life thread, sorted by an honest attention
 *      signal (open assignments, then recency). Each card shows the thread name
 *      upright, a Space-color star whose glow/size encodes engagement, a pulse
 *      ring when recently active, a pending badge, and pattern state as readable
 *      color-coded pills.
 *   3. Tap a card → a bottom sheet (mobile) / modal (desktop) with full stats and
 *      two explicit actions: Open thread / See progress.
 *
 * Fed entirely by the unchanged getHolisticView endpoint. Pro+ gated (free users
 * get a sample teaser, not a bare lock). Reflows as normal DOM on resize/rotate.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Loader2, Lock, X, ChevronRight, Sparkles, Clock, ClipboardList, BarChart3, Link2 } from 'lucide-react';
import { getHolisticView } from '../lib/api';
import UpgradePrompt from './UpgradePrompt';
import NavigatorInsight from './NavigatorInsight';

const POSITIVE_PATTERNS = new Set(['strong_follow_through', 'progress_momentum', 'early_delivery']);

const PATTERN_LABELS = {
  strong_follow_through: 'Strong Follow-Through',
  progress_momentum: 'Progress Momentum',
  early_delivery: 'Early Delivery',
  last_minute_delivery: 'Last-Minute Delivery',
  no_proactive_communication: 'No Proactive Communication',
  inward_focus: 'Inward Focus',
  avoidance: 'Avoidance',
  incomplete_submission: 'Incomplete Submission',
  over_commitment: 'Over-Commitment',
  self_sabotage: 'Self-Sabotage',
  external_blame: 'External Blame',
  isolation: 'Isolation',
  perfectionism: 'Perfectionism',
};

const INSIGHT_META = {
  capacity_warning: { label: 'Capacity Warning', tone: 'amber' },
  progress_correlation: { label: 'Progress Link', tone: 'green' },
  avoidance_pattern: { label: 'Avoidance Pattern', tone: 'red' },
  momentum_building: { label: 'Momentum', tone: 'green' },
  thread_suggestion: { label: 'Suggestion', tone: 'slate' },
  cross_space_pattern: { label: 'Cross-Space', tone: 'amber' },
};

// Per-space palette (entrepreneur) — distinct, ordered so adjacent spaces contrast.
const SPACE_PALETTE = ['#3B82F6', '#10B981', '#A855F7', '#F59E0B', '#EC4899', '#06B6D4', '#F97316', '#8B5CF6'];
const FALLBACK_ARC_COLOR = '#64748B';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function labelForPattern(type) {
  return PATTERN_LABELS[type] || type.replace(/_/g, ' ');
}

function ts(d) {
  if (!d) return 0;
  const t = new Date(d).getTime();
  return isNaN(t) ? 0 : t;
}

function formatRelative(d) {
  const t = ts(d);
  if (!t) return 'No activity yet';
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days <= 0) return 'Active today';
  if (days === 1) return 'Active yesterday';
  if (days < 7) return `Active ${days}d ago`;
  if (days < 35) return `Active ${Math.floor(days / 7)}w ago`;
  return `Last active ${new Date(t).toLocaleDateString()}`;
}

// Pattern pill: positive=green; recurring negative escalates amber(3+)/red(5+); else quiet slate.
function patternPillClass(pat) {
  if (pat.positive) return 'bg-green-500/15 text-green-300 border-green-700/40';
  if (pat.count >= 5) return 'bg-red-500/15 text-red-300 border-red-700/40';
  if (pat.count >= 3) return 'bg-amber-500/15 text-amber-300 border-amber-700/40';
  return 'bg-slate-700/40 text-slate-300 border-slate-600/40';
}

const INSIGHT_TONE_CLASS = {
  amber: 'bg-amber-500/15 text-amber-300 border-amber-700/40',
  green: 'bg-green-500/15 text-green-300 border-green-700/40',
  red: 'bg-red-500/15 text-red-300 border-red-700/40',
  slate: 'bg-slate-700/40 text-slate-300 border-slate-600/40',
};

/**
 * Shape the holistic payload into renderable groups + insight items.
 */
function processData(holistic, spaces, isEntrepreneur) {
  const fullSpaceMap = Object.fromEntries((spaces || []).map(s => [s.id, s]));

  // Visible threads: drop the default catch-all + non-active; Pro users don't
  // see threads that live in entrepreneur-only (non-personal) spaces.
  const visible = (holistic.threads || []).filter(t => {
    if (t.is_default) return false;
    if (t.status && t.status !== 'active') return false;
    if (!isEntrepreneur && t.space_id) {
      const sp = fullSpaceMap[t.space_id];
      if (sp && sp.space_type !== 'personal') return false;
    }
    return true;
  });

  const spaceColors = {};
  if (isEntrepreneur) {
    (spaces || []).forEach((s, i) => { spaceColors[s.id] = SPACE_PALETTE[i % SPACE_PALETTE.length]; });
  }

  // Patterns grouped by thread.
  const patternsByThread = {};
  (holistic.patterns || []).forEach(p => {
    if (!p.thread_id) return;
    (patternsByThread[p.thread_id] = patternsByThread[p.thread_id] || []).push({
      type: p.pattern_type,
      label: labelForPattern(p.pattern_type),
      positive: POSITIVE_PATTERNS.has(p.pattern_type),
      count: p.recurrence_count || 1,
    });
  });

  const metas = visible.map(t => {
    const space = isEntrepreneur && t.space_id ? fullSpaceMap[t.space_id] : null;
    const pending = Number(t.pending_assignments) || 0;
    const completed = Number(t.completed_assignments) || 0;
    const sessions = Number(t.session_count) || 0;
    const arcColor = isEntrepreneur && t.space_id
      ? (spaceColors[t.space_id] || FALLBACK_ARC_COLOR)
      : (t.color || '#3B82F6');
    const pats = (patternsByThread[t.id] || []).sort((a, b) => b.count - a.count);
    return {
      id: t.id,
      name: t.name,
      color: t.color || '#3B82F6',
      arcColor,
      spaceId: t.space_id || null,
      spaceName: space?.name || null,
      sessions,
      pending,
      completed,
      lastActivity: t.last_activity_at,
      recencyTs: ts(t.last_activity_at),
      isRecent: ts(t.last_activity_at) > 0 && (Date.now() - ts(t.last_activity_at)) < WEEK_MS,
      engagement: sessions * 3 + pending + completed + 1,
      patterns: pats,
    };
  });

  // Honest sort: open commitments first, then most recent.
  const cmp = (a, b) => (b.pending - a.pending) || (b.recencyTs - a.recencyTs);

  let groups;
  if (isEntrepreneur && (spaces || []).length > 1) {
    const ordered = (spaces || []).slice().sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
    groups = ordered
      .map(s => ({ space: s, color: spaceColors[s.id] || FALLBACK_ARC_COLOR, threads: metas.filter(m => m.spaceId === s.id).sort(cmp) }))
      .filter(g => g.threads.length > 0);
    const orphan = metas.filter(m => !m.spaceId).sort(cmp);
    if (orphan.length) groups.push({ space: null, color: null, threads: orphan });
  } else {
    groups = [{ space: null, color: null, threads: metas.slice().sort(cmp) }];
  }

  // Insights — only those connecting two or more *visible* threads. Each carries
  // its threads' names + colors so the connection card can show colored dots.
  const idToMeta = Object.fromEntries(metas.map(m => [String(m.id), m]));
  const insightItems = (holistic.insights || [])
    .map((ins, idx) => {
      const threadsForIns = (ins.thread_ids || [])
        .map(id => idToMeta[String(id)])
        .filter(Boolean)
        .map(m => ({ name: m.name, color: m.arcColor }));
      if (threadsForIns.length < 2) return null;
      const meta = INSIGHT_META[ins.insight_type] || { label: (ins.insight_type || 'Insight').replace(/_/g, ' '), tone: 'slate' };
      return { key: `ins-${idx}`, label: meta.label, tone: meta.tone, text: ins.insight_text, threads: threadsForIns };
    })
    .filter(Boolean);

  return { groups, insightItems, threadCount: metas.length };
}

function PatternPills({ patterns, max = 3 }) {
  if (!patterns?.length) return null;
  const shown = patterns.slice(0, max);
  const extra = patterns.length - shown.length;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {shown.map((p, i) => (
        <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full border ${patternPillClass(p)}`}>
          {p.label}{p.count > 1 ? ` ·${p.count}×` : ''}
        </span>
      ))}
      {extra > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700/50">+{extra}</span>}
    </div>
  );
}

function ThreadStar({ meta }) {
  const size = Math.max(10, Math.min(10 + meta.engagement * 1.2, 22));
  const glow = Math.min(meta.engagement * 1.8, 22);
  return (
    <span className="relative flex items-center justify-center flex-shrink-0" style={{ width: 24, height: 24 }}>
      {meta.isRecent && (
        <span
          className="absolute rounded-full animate-ping opacity-60"
          style={{ width: size, height: size, backgroundColor: meta.arcColor, opacity: 0.35 }}
        />
      )}
      <span
        className="relative rounded-full"
        style={{ width: size, height: size, backgroundColor: meta.arcColor, boxShadow: `0 0 ${glow}px ${meta.arcColor}66` }}
      />
    </span>
  );
}

function ThreadCard({ meta, isEntrepreneur, onSelect }) {
  return (
    <button
      onClick={() => onSelect(meta)}
      className="w-full text-left bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 hover:border-slate-600 rounded-xl p-4 shadow-soft hover:shadow-glow transition-all duration-200 hover:-translate-y-0.5 min-h-tap"
    >
      <div className="flex items-center gap-3">
        <ThreadStar meta={meta} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-200 truncate">{meta.name}</span>
            {isEntrepreneur && meta.spaceName && (
              <span className="text-[11px] text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded flex-shrink-0">{meta.spaceName}</span>
            )}
          </div>
          <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500 mt-1">
            <span>{meta.sessions} session{meta.sessions !== 1 ? 's' : ''}</span>
            {meta.pending > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-400">
                <ClipboardList size={11} /> {meta.pending} pending
              </span>
            )}
            {meta.completed > 0 && <span>{meta.completed} done</span>}
            <span className="text-slate-600">{formatRelative(meta.lastActivity)}</span>
          </div>
        </div>
        <ChevronRight size={16} className="text-slate-600 flex-shrink-0" />
      </div>
      <PatternPills patterns={meta.patterns} />
    </button>
  );
}

function ConnectStrip({ insights }) {
  if (!insights.length) return null;
  return (
    <div>
      <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <Link2 size={13} className="text-slate-500" /> How your threads connect
      </h3>
      <div className="space-y-3">
        {insights.map(ins => (
          <div key={ins.key} className="bg-slate-800/40 border border-slate-700/50 rounded-lg px-3.5 py-3">
            <div className="flex items-center gap-x-2 gap-y-1 flex-wrap mb-1.5">
              {ins.threads.map((t, i) => (
                <span key={i} className="inline-flex items-center text-sm text-slate-200">
                  {i > 0 && <span className="text-slate-600 mx-1.5">⟷</span>}
                  <span className="w-2 h-2 rounded-full flex-shrink-0 mr-1.5" style={{ backgroundColor: t.color }} />
                  {t.name}
                </span>
              ))}
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ml-auto flex-shrink-0 ${INSIGHT_TONE_CLASS[ins.tone] || INSIGHT_TONE_CLASS.slate}`}>
                {ins.label}
              </span>
            </div>
            {ins.text && <p className="text-xs text-slate-400 leading-relaxed">{ins.text}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ThreadSheet({ meta, isEntrepreneur, onClose, onOpenThread, onViewProgress }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!meta) return null;
  return (
    <div className="fixed inset-0 z-30 flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label={meta.name}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-slate-900 border border-slate-700 rounded-t-2xl sm:rounded-2xl shadow-soft p-5 pb-safe-2 animate-slide-up max-h-[85vh] overflow-y-auto">
        <div className="flex items-start gap-3 mb-4">
          <ThreadStar meta={meta} />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-slate-100 truncate">{meta.name}</h2>
            {isEntrepreneur && meta.spaceName && <p className="text-xs text-slate-500 mt-0.5">{meta.spaceName}</p>}
            <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><Clock size={11} /> {formatRelative(meta.lastActivity)}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-500 hover:text-slate-300 p-1 -m-1">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            { label: 'Sessions', value: meta.sessions },
            { label: 'Pending', value: meta.pending, accent: meta.pending > 0 ? 'text-amber-400' : '' },
            { label: 'Completed', value: meta.completed },
          ].map(s => (
            <div key={s.label} className="bg-slate-800/50 border border-slate-700/40 rounded-lg p-3 text-center">
              <div className={`text-lg font-semibold ${s.accent || 'text-slate-200'}`}>{s.value}</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>

        {meta.patterns.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-slate-400 mb-1.5">Patterns</p>
            <div className="flex flex-wrap gap-1.5">
              {meta.patterns.map((p, i) => (
                <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full border ${patternPillClass(p)}`}>
                  {p.label}{p.count > 1 ? ` ·${p.count}×` : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={() => { onOpenThread?.(meta.id); onClose(); }}
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg py-2.5 text-sm font-medium transition-colors min-h-tap"
          >
            Open thread <ChevronRight size={15} />
          </button>
          <button
            onClick={() => { onViewProgress?.(meta.id); onClose(); }}
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg py-2.5 text-sm font-medium transition-colors min-h-tap"
          >
            <BarChart3 size={15} /> See progress
          </button>
        </div>
      </div>
    </div>
  );
}

const SAMPLE_TEASER = [
  { id: 's1', name: 'Career', arcColor: '#3B82F6', sessions: 8, pending: 2, completed: 4, engagement: 30, isRecent: true, lastActivity: new Date().toISOString(), patterns: [{ label: 'Strong Follow-Through', positive: true, count: 3 }] },
  { id: 's2', name: 'Health', arcColor: '#10B981', sessions: 5, pending: 1, completed: 2, engagement: 18, isRecent: false, lastActivity: null, patterns: [{ label: 'Avoidance', positive: false, count: 4 }] },
  { id: 's3', name: 'Finances', arcColor: '#F59E0B', sessions: 3, pending: 0, completed: 1, engagement: 11, isRecent: false, lastActivity: null, patterns: [] },
];

export default function ContextMap({ subscription, spaces = [], driveLevel = 'mentor', onOpenThread, onViewProgress }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [holistic, setHolistic] = useState(null);
  const [selected, setSelected] = useState(null);

  const isPro = subscription?.tier === 'pro' || subscription?.tier === 'entrepreneur';
  const isEntrepreneur = subscription?.tier === 'entrepreneur';

  useEffect(() => {
    if (!isPro) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await getHolisticView();
        if (!cancelled) setHolistic(data);
      } catch {
        if (!cancelled) setError('Failed to load your map.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isPro]);

  const { groups, insightItems, threadCount } = useMemo(
    () => holistic ? processData(holistic, spaces, isEntrepreneur) : { groups: [], insightItems: [], threadCount: 0 },
    [holistic, spaces, isEntrepreneur]
  );

  const closeSheet = useCallback(() => setSelected(null), []);

  // Free tier — sample teaser instead of a bare lock.
  if (!isPro) {
    return (
      <div className="absolute inset-0 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center mb-6">
            <h2 className="text-lg font-display font-semibold tracking-tight text-slate-200">Your Map</h2>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              See how your life areas connect — where momentum is building, where patterns cluster, and what needs your attention.
            </p>
          </div>
          <div className="relative">
            <div className="space-y-3 pointer-events-none select-none blur-[1.5px] opacity-60">
              {SAMPLE_TEASER.map(m => (
                <div key={m.id} className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <ThreadStar meta={m} />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-slate-200">{m.name}</span>
                      <div className="text-[11px] text-slate-500 mt-1">{m.sessions} sessions · {m.pending} pending</div>
                    </div>
                  </div>
                  <PatternPills patterns={m.patterns} />
                </div>
              ))}
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-slate-900/80 border border-slate-700/60 rounded-xl px-4 py-3 flex items-center gap-2">
                <Lock size={15} className="text-slate-400" />
                <span className="text-sm text-slate-300">Unlock with Pro</span>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <UpgradePrompt
              limitError={{
                error: 'Your Map is a Pro feature',
                upgrade_tier: 'pro',
                upgrade_message: 'Upgrade to Pro to see your life threads, patterns, and the connections between them.',
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Loader2 size={24} className="text-blue-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  if (threadCount === 0) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
        <div className="w-16 h-16 rounded-full border-2 border-dashed border-slate-700 flex items-center justify-center mb-4">
          <Sparkles size={20} className="text-slate-600" />
        </div>
        <h2 className="text-lg font-display font-semibold tracking-tight text-slate-300 mb-2">Your Map</h2>
        <p className="text-sm text-slate-500 max-w-sm">
          Once you have a few sessions across different areas, this view shows how your threads,
          patterns, and assignments connect.
        </p>
        <p className="text-xs text-slate-600 mt-3">
          Open the <span className="text-slate-400">Coach</span> tab to start your first session.
        </p>
      </div>
    );
  }

  const hasConnections = insightItems.length > 0;

  return (
    <div className="absolute inset-0 overflow-y-auto">
      <div className={`${hasConnections ? 'max-w-4xl' : 'max-w-2xl'} mx-auto px-4 py-6`}>
        <div className="mb-5 animate-fade-in">
          <h2 className="text-lg font-display font-semibold tracking-tight text-slate-200">Your Map</h2>
          <p className="text-xs text-slate-500 mt-0.5">The shape of your life right now.</p>
        </div>

        {/* Synthesis hero — the Navigator's single sharpest read on you. Renders
            nothing when there's no real observation. */}
        <NavigatorInsight
          insights={holistic?.insights}
          patterns={holistic?.patterns}
          currentRead={holistic?.current_read}
          driveLevel={driveLevel}
          variant="hero"
          className="mb-6 animate-fade-in"
        />

        {/* Narrative (connections) + triage (threads). Side-by-side on desktop
            when there are connections; stacked otherwise. */}
        <div className={hasConnections ? 'md:grid md:grid-cols-5 md:gap-6' : ''}>
          {hasConnections && (
            <div
              className="mb-6 md:mb-0 md:col-span-2 animate-fade-in"
              style={{ animationDelay: '80ms', animationFillMode: 'backwards' }}
            >
              <ConnectStrip insights={insightItems} />
            </div>
          )}

          <div
            className={`animate-fade-in ${hasConnections ? 'md:col-span-3' : ''}`}
            style={{ animationDelay: '160ms', animationFillMode: 'backwards' }}
          >
            <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Your threads</h3>
            {groups.map((group, gi) => (
              <div key={group.space?.id || `g-${gi}`} className="mb-6">
                {group.space && (
                  <h3 className="text-xs font-medium uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: group.color || '#94A3B8' }}>
                    {group.space.icon && <span>{group.space.icon}</span>}
                    {group.space.name}
                    <span className="text-slate-600">({group.threads.length})</span>
                  </h3>
                )}
                <div className="space-y-3">
                  {group.threads.map(meta => (
                    <ThreadCard key={meta.id} meta={meta} isEntrepreneur={isEntrepreneur} onSelect={setSelected} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selected && (
        <ThreadSheet
          meta={selected}
          isEntrepreneur={isEntrepreneur}
          onClose={closeSheet}
          onOpenThread={onOpenThread}
          onViewProgress={onViewProgress}
        />
      )}
    </div>
  );
}
